import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Use relative URLs (/api) whenever the app is NOT on localhost.
// This covers receppet.com.br, emergent.host, and any future custom domain.
// Only use the absolute BACKEND_URL for local development (localhost/127.0.0.1).
const isLocalDev = typeof window !== 'undefined' && 
    (window.location.hostname === 'localhost' || 
     window.location.hostname === '127.0.0.1');

const API_URL = isLocalDev ? `${BACKEND_URL}/api` : '/api';

const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 30000,
});

// Dedicated instance for file uploads with longer timeout
export const uploadApi = axios.create({
    baseURL: API_URL,
    timeout: 60000,
});

// Add auth token to upload requests too
uploadApi.interceptors.request.use((config) => {
    const token = safeLocalStorage.getItem('receppet_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Safe localStorage access helper
const safeLocalStorage = {
    getItem: (key) => {
        try {
            return localStorage.getItem(key);
        } catch (e) {
            console.warn('localStorage not available');
            return null;
        }
    },
    removeItem: (key) => {
        try {
            localStorage.removeItem(key);
        } catch (e) {
            console.warn('localStorage not available');
        }
    }
};

/**
 * Extract error message from API response safely
 * Handles Pydantic validation errors which return objects like {type, loc, msg, input, url}
 */
export const getErrorMessage = (error, defaultMessage = 'Erro ao processar solicitação') => {
    // Network error (no response from server)
    if (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error') {
        return 'Servidor temporariamente indisponível. Verifique sua conexão e tente novamente.';
    }
    
    // Timeout
    if (error?.code === 'ECONNABORTED') {
        return 'A requisição demorou demais. Tente novamente.';
    }
    
    // If it's an axios error with response
    const detail = error?.response?.data?.detail;
    
    if (!detail) {
        return error?.message || defaultMessage;
    }
    
    // If detail is a string, return it directly
    if (typeof detail === 'string') {
        return detail;
    }
    
    // Translation map for common Pydantic errors
    const errorTranslations = {
        'Field required': 'Campo obrigatório',
        'Value error, Rating deve ser entre 1 e 5': 'A nota deve ser entre 1 e 5 estrelas',
        'value is not a valid integer': 'Valor inválido',
        'value is not a valid email address': 'E-mail inválido',
    };
    
    // If detail is an array (Pydantic validation errors)
    if (Array.isArray(detail)) {
        const messages = detail.map(e => {
            if (typeof e === 'string') return errorTranslations[e] || e;
            if (e?.msg) {
                // Get field name for better error message
                const fieldName = e.loc?.slice(-1)?.[0];
                const translatedMsg = errorTranslations[e.msg] || e.msg;
                
                // Map field names to Portuguese
                const fieldTranslations = {
                    'rating': 'nota',
                    'booking_id': 'reserva',
                    'comment': 'comentário',
                    'email': 'e-mail',
                    'password': 'senha',
                };
                
                if (fieldName && translatedMsg.includes('obrigatório')) {
                    const ptFieldName = fieldTranslations[fieldName] || fieldName;
                    return `O campo "${ptFieldName}" é obrigatório`;
                }
                return translatedMsg;
            }
            if (e?.message) return errorTranslations[e.message] || e.message;
            return defaultMessage;
        });
        
        // Remove duplicates and join
        return [...new Set(messages)].join('. ');
    }
    
    // If detail is an object (single Pydantic error)
    if (typeof detail === 'object') {
        if (detail.msg) return errorTranslations[detail.msg] || detail.msg;
        if (detail.message) return errorTranslations[detail.message] || detail.message;
        // Don't try to render the object itself
        return defaultMessage;
    }
    
    return defaultMessage;
};

// Add token to requests
api.interceptors.request.use((config) => {
    const token = safeLocalStorage.getItem('receppet_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Don't clear storage or redirect if on protected auth pages
            const isAuthPage = window.location.pathname.includes('/login') || 
                               window.location.pathname.includes('/register') ||
                               window.location.pathname.includes('/auth') ||
                               window.location.pathname.includes('/verification');
            
            if (!isAuthPage) {
                safeLocalStorage.removeItem('receppet_token');
                safeLocalStorage.removeItem('receppet_user');
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

// ============================================
// Smart Cache System for Performance
// ============================================
const apiCache = new Map();
const CACHE_TTL = 30000; // 30 seconds

/**
 * Check if cache is valid
 */
const isCacheValid = (key) => {
    const cached = apiCache.get(key);
    if (!cached) return false;
    return Date.now() - cached.timestamp < CACHE_TTL;
};

/**
 * Cached GET request - reduces redundant API calls
 * @param {string} url - API endpoint
 * @param {object} options - Optional axios config
 * @param {number} ttl - Custom TTL in milliseconds
 */
export const cachedGet = async (url, options = {}, ttl = CACHE_TTL) => {
    const cacheKey = `${url}_${JSON.stringify(options.params || {})}`;
    
    if (isCacheValid(cacheKey)) {
        return apiCache.get(cacheKey).data;
    }
    
    const response = await api.get(url, options);
    apiCache.set(cacheKey, {
        data: response,
        timestamp: Date.now()
    });
    
    return response;
};

/**
 * Invalidate cache for specific URL pattern
 */
export const invalidateCache = (urlPattern) => {
    for (const key of apiCache.keys()) {
        if (key.includes(urlPattern)) {
            apiCache.delete(key);
        }
    }
};

/**
 * Clear all cache
 */
export const clearCache = () => {
    apiCache.clear();
};

// Auth
export const authAPI = {
    register: (data) => api.post('/auth/register', data),
    login: (data) => api.post('/auth/login', data),
    getMe: () => api.get('/auth/me'),
    logout: () => api.post('/auth/logout'),
    verifyEmail: (code) => api.post('/auth/verify-email', { code }),
    verifyPhone: (code) => api.post('/auth/verify-phone', { code }),
    resendVerification: (type) => api.post(`/auth/resend-verification?verification_type=${type}`),
    googleCallback: (sessionId) => api.post('/auth/google/callback', { session_id: sessionId }),
};

// Users
export const usersAPI = {
    updateMe: (data) => api.put('/users/me', data),
    uploadDocument: (data) => api.post('/users/documents', data),
    getUser: (userId) => api.get(`/users/${userId}`),
    listProviders: (params) => api.get('/providers', { params }),
};

// Pets
export const petsAPI = {
    create: (data) => api.post('/pets', data),
    list: () => api.get('/pets'),
    get: (petId) => api.get(`/pets/${petId}`),
    update: (petId, data) => api.put(`/pets/${petId}`, data),
    delete: (petId) => api.delete(`/pets/${petId}`),
};

// Services
export const servicesAPI = {
    create: (data) => api.post('/services', data),
    list: (params) => api.get('/services', { params }),
    listMine: () => api.get('/services/my'),
    get: (serviceId) => api.get(`/services/${serviceId}`),
    update: (serviceId, data) => api.put(`/services/${serviceId}`, data),
    delete: (serviceId) => api.delete(`/services/${serviceId}`),
    pause: (serviceId, paused = true) => api.put(`/services/${serviceId}/pause?paused=${paused}`),
    setAvailability: (serviceId, slots) => api.post(`/services/${serviceId}/availability`, slots),
    getAvailability: (serviceId, month) => api.get(`/services/${serviceId}/availability`, { params: { month } }),
    // Extras CRUD
    listExtras: (serviceId) => api.get(`/services/${serviceId}/extras`),
    addExtra: (serviceId, data) => api.post(`/services/${serviceId}/extras`, data),
    updateExtra: (serviceId, extraId, data) => api.put(`/services/${serviceId}/extras/${extraId}`, data),
    deleteExtra: (serviceId, extraId) => api.delete(`/services/${serviceId}/extras/${extraId}`),
};

// Bookings
export const bookingsAPI = {
    create: (data) => api.post('/bookings', data),
    list: () => api.get('/bookings'),
    get: (bookingId) => api.get(`/bookings/${bookingId}`),
    updateStatus: (bookingId, status, reason = null) => {
        let url = `/bookings/${bookingId}/status?status=${status}`;
        if (reason) url += `&reason=${encodeURIComponent(reason)}`;
        return api.put(url);
    },
    delete: (bookingId) => api.delete(`/bookings/${bookingId}`),
};

// Payments (Mercado Pago - Checkout Transparente)
export const paymentsAPI = {
    // Unified payment endpoint
    createPayment: (data) => api.post('/payments/create', data),
    // PIX specific
    createPix: (bookingId, payerData) => api.post('/payments/pix/create', { 
        booking_id: bookingId, 
        ...payerData 
    }),
    getPixStatus: (paymentId) => api.get(`/payments/pix/status/${paymentId}`),
    // Generic status check for all payment methods
    getPaymentStatus: (paymentId) => api.get(`/payments/status/${paymentId}`),
    // Payment methods and installments
    getMethods: () => api.get('/payments/methods'),
    getInstallments: (amount, bin) => api.get('/payments/installments', { params: { amount, bin } }),
    getCardIssuers: (paymentMethodId) => api.get(`/payments/card-issuers/${paymentMethodId}`),
    // Legacy methods
    refund: (bookingId, reason) => api.post('/payments/refund', { booking_id: bookingId, reason }),
    getHistory: () => api.get('/payments/history'),
};

// Reviews
export const reviewsAPI = {
    create: (data) => api.post('/reviews', data),
    getByService: (serviceId) => api.get(`/reviews/service/${serviceId}`),
    getByProvider: (providerId) => api.get(`/reviews/provider/${providerId}`),
    getByTutor: (tutorId) => api.get(`/reviews/tutor/${tutorId}`),
    getPending: () => api.get('/reviews/pending'),
    getByBooking: (bookingId) => api.get(`/reviews/booking/${bookingId}`),
};

// Chat
export const chatAPI = {
    sendMessage: (data) => api.post('/chat/send', data),
    getConversation: (userId) => api.get(`/chat/history/${userId}`),
    getConversations: () => api.get('/chat/conversations'),
    markMessageRead: (messageId) => api.put(`/messages/${messageId}/read`),
    // Typing indicators
    setTyping: (receiverId, isTyping) => api.post('/messages/typing', { receiver_id: receiverId, is_typing: isTyping }),
    getTypingStatus: (userId) => api.get(`/messages/typing/${userId}`),
    // Heartbeat for online status
    heartbeat: () => api.post('/auth/heartbeat'),
    // Chat access check
    canChat: (userId) => api.get(`/chat/can-chat/${userId}`),
};

// Admin
export const adminAPI = {
    getPendingApprovals: () => api.get('/admin/pending-approvals'),
    approveUser: (userId, approved) => api.put(`/admin/approve/${userId}?approved=${approved}`),
    approveProvider: (userId) => api.put(`/admin/approve/${userId}?approved=true`),
    rejectProvider: (userId, reason) => api.put(`/admin/approve/${userId}?approved=false`, { reason }),
    suspendUser: (userId, suspend) => api.put(`/admin/suspend/${userId}?suspend=${suspend}`),
    listUsers: () => api.get('/admin/users'),
    getUserDocuments: (userId) => api.get(`/admin/user/${userId}/documents`),
    getStats: () => api.get('/admin/stats'),
    listBookings: (search) => api.get('/admin/bookings', { params: search ? { search } : {} }),
    getBookingDetails: (bookingId) => api.get(`/admin/bookings/${bookingId}/details`),
    getLogs: (limit) => api.get('/admin/logs', { params: { limit } }),
    // Service approval
    getPendingServices: () => api.get('/admin/pending-services'),
    approveService: (serviceId, approved) => api.put(`/admin/approve-service/${serviceId}?approved=${approved}`),
    listServices: () => api.get('/admin/services'),
    // Messaging monitoring
    getBlockedMessages: (limit) => api.get('/admin/blocked-messages', { params: { limit } }),
    getAllMessages: (limit) => api.get('/admin/messages', { params: { limit } }),
    // CRUD operations
    deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
    updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
    deleteService: (serviceId) => api.delete(`/admin/services/${serviceId}`),
    updateService: (serviceId, data) => api.put(`/admin/services/${serviceId}`, data),
    deleteBooking: (bookingId) => api.delete(`/admin/bookings/${bookingId}`),
    updateBooking: (bookingId, data) => api.put(`/admin/bookings/${bookingId}`, data),
    // Platform Settings
    getSettings: () => api.get('/admin/settings'),
    updateSettings: (settings) => api.put('/admin/settings', settings),
    // Financial Reports
    getFinanceSummary: () => api.get('/admin/finance/summary'),
    getMonthlyRevenue: (months = 6) => api.get('/admin/finance/monthly', { params: { months } }),
    getRevenueByServiceType: () => api.get('/admin/finance/by-service-type'),
    getTopProviders: (limit = 10) => api.get('/admin/finance/top-providers', { params: { limit } }),
};

// Administrator Management API
export const administratorsAPI = {
    list: () => api.get('/admin/administrators'),
    create: (data) => api.post('/admin/administrators', data),
    update: (adminId, data) => api.put(`/admin/administrators/${adminId}`, data),
    changePassword: (adminId, data) => api.put(`/admin/administrators/${adminId}/password`, data),
    toggleStatus: (adminId, status) => api.put(`/admin/administrators/${adminId}/status`, { status }),
    delete: (adminId) => api.delete(`/admin/administrators/${adminId}`),
    getMe: () => api.get('/admin/administrators/me'),
};

// Secure Messaging
export const secureMessagesAPI = {
    send: (receiverId, content, bookingId = null, messageType = 'text', imageUrl = null) => api.post('/messages', { 
        receiver_id: receiverId, 
        content, 
        booking_id: bookingId,
        message_type: messageType,
        image_url: imageUrl,
    }),
    getConversation: (userId) => api.get(`/messages/conversation/${userId}`),
    markRead: (userId) => api.put(`/messages/conversation/${userId}/read`),
};

// AI
export const aiAPI = {
    generateDescription: (data) => api.post('/ai/generate-description', data),
};

// Notifications
export const notificationsAPI = {
    list: (unreadOnly = false, limit = 50) => api.get('/notifications', { params: { unread_only: unreadOnly, limit } }),
    markRead: (notificationId) => api.put(`/notifications/${notificationId}/read`),
    markAllRead: () => api.put('/notifications/read-all'),
};

// Push Notifications
export const pushAPI = {
    subscribe: (subscription) => api.post('/push/subscribe', subscription),
    unsubscribe: (endpoint) => api.delete('/push/unsubscribe', { params: { endpoint } }),
    status: () => api.get('/push/status'),
    firebaseStatus: () => api.get('/push/firebase-status'),
    sendTest: () => api.post('/push/test'),
    // FCM
    registerFcmToken: (token, deviceType = 'web') => api.post('/push/fcm/register', { token, device_type: deviceType }),
    unregisterFcmToken: (token) => api.delete('/push/fcm/unregister', { params: { token } }),
    getFcmTokens: () => api.get('/push/fcm/tokens'),
};

// Availability
export const availabilityAPI = {
    getMyAvailability: () => api.get('/availability/me'),
    getProviderAvailability: (providerId) => api.get(`/availability/provider/${providerId}`),
    getServiceAvailability: (serviceId) => api.get(`/availability/service/${serviceId}`),
    getServiceBusyHours: (serviceId, date) => api.get(`/availability/service/${serviceId}/hours/${date}`),
    blockDate: (date, reason = null) => api.post('/availability/block', { date, reason }),
    blockDateRange: (startDate, endDate, reason = null) => api.post('/availability/block-range', { start_date: startDate, end_date: endDate, reason }),
    unblockDate: (date) => api.delete(`/availability/unblock/${date}`),
    unblockDateRange: (startDate, endDate) => api.delete('/availability/unblock-range', { params: { start_date: startDate, end_date: endDate } }),
};

// Favorites
export const favoritesAPI = {
    list: () => api.get('/favorites'),
    add: (itemType, itemId) => api.post('/favorites', { item_type: itemType, item_id: itemId }),
    remove: (itemType, itemId) => api.delete(`/favorites/${itemType}/${itemId}`),
    check: (itemType, itemId) => api.get(`/favorites/check/${itemType}/${itemId}`),
    count: () => api.get('/favorites/count'),
};

// Utility
export const utilityAPI = {
    lookupCep: (cep) => api.get(`/cep/${cep}`),
    calculateDistance: (origin, destination) => api.post('/distance/calculate', { origin, destination }),
};

// Referral System (P3)
export const referralAPI = {
    generate: () => api.post('/referral/generate'),
    apply: (code) => api.post(`/referral/apply?code=${code}`),
    getStats: () => api.get('/referral/stats'),
};

// Credits
export const creditsAPI = {
    getBalance: () => api.get('/credits/balance'),
};

// Geolocation (P3)
export const geoAPI = {
    updateLocation: (latitude, longitude) => api.post('/location/update', { latitude, longitude }),
    getNearbyServices: (latitude, longitude, radiusKm = 10, serviceType = null, limit = 20) => {
        const params = { latitude, longitude, radius_km: radiusKm, limit };
        if (serviceType && serviceType !== 'all') params.service_type = serviceType;
        return api.get('/services/nearby', { params });
    },
    updateServiceLocation: (serviceId, latitude, longitude) => api.post(`/services/${serviceId}/location`, { latitude, longitude }),
};

// Anti-Fraud (P3) - Admin only
export const antifraudAPI = {
    logAttempt: (email, ip, userAgent, success) => api.post('/antifraud/log-attempt', { email, ip, user_agent: userAgent, success }),
    checkBooking: (bookingId) => api.get('/antifraud/check-booking', { params: { booking_id: bookingId } }),
    listFlags: (resolved = null) => api.get('/admin/fraud-flags', { params: { resolved } }),
    resolveFlag: (flagId) => api.put(`/admin/fraud-flags/${flagId}/resolve`),
};

// Provider Documents
export const documentsAPI = {
    upload: (documentType, frontImage, backImage) => api.post('/documents/upload', { 
        document_type: documentType, 
        front_image: frontImage, 
        back_image: backImage 
    }),
    getMy: () => api.get('/documents/my'),
    // Admin
    getPending: () => api.get('/admin/documents/pending'),
    review: (docId, approved, rejectionReason = null) => api.put(`/admin/documents/${docId}/review`, null, { 
        params: { approved, rejection_reason: rejectionReason } 
    }),
};

// Support Tickets
export const supportAPI = {
    createTicket: (category, subject, message) => api.post('/support/tickets', { category, subject, message }),
    getMyTickets: () => api.get('/support/tickets/my'),
    getTicket: (ticketId) => api.get(`/support/tickets/${ticketId}`),
    addMessage: (ticketId, message) => api.post(`/support/tickets/${ticketId}/message`, { message }),
    // Admin
    getAllTickets: () => api.get('/admin/support/tickets'),
    getTicketStats: () => api.get('/admin/support/tickets/stats'),
    updateTicketStatus: (ticketId, status) => api.put(`/support/tickets/${ticketId}/status`, null, { params: { status } }),
};

// Detailed Reviews (Airbnb-style)
export const detailedReviewsAPI = {
    create: (bookingId, criteria) => api.post(`/reviews/detailed?booking_id=${bookingId}`, criteria),
    getForUser: (userId) => api.get(`/reviews/detailed/user/${userId}`),
    getForBooking: (bookingId) => api.get(`/reviews/detailed/booking/${bookingId}`),
};

// Admin Booking Management
export const adminBookingsAPI = {
    updateStatuses: () => api.post('/admin/bookings/update-statuses'),
    // Payout management
    getPayoutStats: () => api.get('/admin/payouts/stats'),
    getPendingPayouts: () => api.get('/admin/payouts/pending'),
    confirmPayout: (bookingId) => api.put(`/admin/bookings/${bookingId}/payout`),
    triggerAutoComplete: () => api.post('/admin/bookings/auto-complete'),
    releaseAvailableFunds: () => api.post('/admin/payouts/release-available'),
    // Real-time visibility
    getRecentPayments: () => api.get('/admin/payments/recent'),
    // Reconciliation (Cron Job)
    getReconciliationLogs: (limit = 50, logType = null) => 
        api.get('/admin/reconciliation/logs', { params: { limit, log_type: logType } }),
    getReconciliationStats: () => api.get('/admin/reconciliation/stats'),
    triggerReconciliation: () => api.post('/admin/reconciliation/run-now'),
};

export default api;
